import type { OrderCard, OrderDraft, OrderHandoff, OrderServiceMode } from "@khazzin/api-client";
import type { Translate } from "@khazzin/i18n";

/**
 * The New Order forms' order-type boxes and service mode (ADJ-1022-3), as
 * values: shared by the console's form and the Store Portal's, which each
 * render {@link OrderTypeCards} over them.
 *
 * **Pure.** The rules the server makes again — which boxes combine, what each
 * asks, what the request carries — are functions of the values here, so they
 * are tested without a screen. The server's answer still wins: its refusal
 * lands under the same field, because the keys are the wire's names.
 */

/** One item an exchange expects back, as the form holds it. */
export interface ExpectedReturnRow {
	rowId: string;
	sku: string;
	name: string;
	qty: number;
	notes: string;
}

export interface OrderCardsValue {
	cards: OrderCard[];
	/** What the operator chose; a design pickup overrides it ({@link effectiveMode}). */
	serviceMode: OrderServiceMode;
	handoff: OrderHandoff;
	pickupWarehouseId: string;
	pickupInstructions: string;
	exchangeNotes: string;
	expectedReturns: ExpectedReturnRow[];
	pickupDetails: string;
	/** The cash our driver pays the customer on a design pickup; null is none. */
	designRefundMinor: number | null;
}

/** The three boxes, in the order the form lists them. */
export const ORDER_CARDS: readonly OrderCard[] = ["pickup", "exchange", "pickup_design"];

export const SERVICE_MODES: readonly OrderServiceMode[] = ["fulfillment_delivery", "delivery_only"];

export const HANDOFFS: readonly OrderHandoff[] = ["store_drops", "driver_collects"];

export function emptyOrderCards(
	serviceMode: OrderServiceMode = "fulfillment_delivery",
): OrderCardsValue {
	return {
		cards: [],
		serviceMode,
		handoff: "store_drops",
		pickupWarehouseId: "",
		pickupInstructions: "",
		exchangeNotes: "",
		expectedReturns: [],
		pickupDetails: "",
		designRefundMinor: null,
	};
}

/** The order's mode: a design pickup is delivery only, whatever was chosen. */
export function effectiveMode(value: OrderCardsValue): OrderServiceMode {
	return value.cards.includes("pickup_design") ? "delivery_only" : value.serviceMode;
}

/** The customer collects at our warehouse: no address, no carrier leg. */
export function collectedOnSite(value: OrderCardsValue): boolean {
	return value.cards.includes("pickup");
}

/** Khazzin picks the order from stock: lines are a pick list, packed by us. */
export function usesStock(value: OrderCardsValue): boolean {
	return effectiveMode(value) !== "delivery_only";
}

/**
 * What stops a box from being ticked now, or null — the server's
 * `order_types_conflict` pairs: a design pickup stands alone, and a pickup is
 * served from stock a delivery-only order does not have.
 */
export function cardBlockedBy(
	card: OrderCard,
	value: OrderCardsValue,
): OrderCard | "delivery_only" | null {
	if (value.cards.includes(card)) {
		return null;
	}
	if (card === "pickup_design") {
		return value.cards.find((c) => c === "pickup" || c === "exchange") ?? null;
	}
	if (value.cards.includes("pickup_design")) {
		return "pickup_design";
	}
	if (card === "pickup" && value.serviceMode === "delivery_only") {
		return "delivery_only";
	}
	return null;
}

/** The value with one box ticked or unticked; a blocked box is left as it is. */
export function toggleCard(value: OrderCardsValue, card: OrderCard, on: boolean): OrderCardsValue {
	if (on === value.cards.includes(card) || (on && cardBlockedBy(card, value) !== null)) {
		return value;
	}
	const cards = on ? [...value.cards, card] : value.cards.filter((c) => c !== card);
	return { ...value, cards: ORDER_CARDS.filter((c) => cards.includes(c)) };
}

/** A delivery-only order whose parcel reaches us one way or the other. */
export function asksHandoff(value: OrderCardsValue): boolean {
	return effectiveMode(value) === "delivery_only" && !value.cards.includes("pickup_design");
}

/**
 * The boxes' own questions, mirroring intake's (`field_required`): a pickup
 * names its warehouse, an exchange at least one expected item, a design
 * pickup its materials.
 */
export function validateOrderCards(value: OrderCardsValue, t: Translate): Record<string, string> {
	const errors: Record<string, string> = {};
	if (value.cards.includes("pickup") && value.pickupWarehouseId === "") {
		errors.pickupWarehouseId = t("orderCards.errors.warehouse");
	}
	if (value.cards.includes("exchange") && value.expectedReturns.length === 0) {
		errors.expectedReturns = t("orderCards.errors.expectedReturns");
	}
	if (value.cards.includes("pickup_design") && value.pickupDetails.trim() === "") {
		errors.pickupDetails = t("orderCards.errors.pickupDetails");
	}
	return errors;
}

type CardsDraft = Pick<
	OrderDraft,
	| "cards"
	| "serviceMode"
	| "handoff"
	| "pickupWarehouseId"
	| "pickupInstructions"
	| "exchangeNotes"
	| "expectedReturns"
	| "pickupDetails"
	| "designRefundMinor"
>;

/**
 * The request's half that the boxes fill. Only the ticked boxes' answers are
 * sent: an unticked box's leftovers are not the order's.
 */
export function orderCardsDraft(value: OrderCardsValue): CardsDraft {
	const draft: CardsDraft = { serviceMode: effectiveMode(value) };
	const has = (card: OrderCard) => value.cards.includes(card);
	if (value.cards.length > 0) {
		draft.cards = [...value.cards];
	}
	if (asksHandoff(value)) {
		draft.handoff = value.handoff;
	}
	if (has("pickup") && value.pickupWarehouseId !== "") {
		draft.pickupWarehouseId = value.pickupWarehouseId;
	}
	if ((has("pickup") || has("pickup_design")) && value.pickupInstructions.trim() !== "") {
		draft.pickupInstructions = value.pickupInstructions.trim();
	}
	if (has("exchange")) {
		if (value.exchangeNotes.trim() !== "") {
			draft.exchangeNotes = value.exchangeNotes.trim();
		}
		draft.expectedReturns = value.expectedReturns.map((row) => ({
			sku: row.sku,
			qty: row.qty,
			...(row.notes.trim() === "" ? {} : { notes: row.notes.trim() }),
		}));
	}
	if (has("pickup_design")) {
		draft.pickupDetails = value.pickupDetails.trim();
		if ((value.designRefundMinor ?? 0) > 0) {
			draft.designRefundMinor = value.designRefundMinor ?? 0;
		}
	}
	return draft;
}

/**
 * The expected items with one more: a SKU already on the list gains the
 * quantity rather than a second row.
 */
export function addExpectedReturn(
	rows: readonly ExpectedReturnRow[],
	row: Omit<ExpectedReturnRow, "rowId">,
): ExpectedReturnRow[] {
	const at = rows.findIndex((r) => r.sku === row.sku);
	if (at === -1) {
		return [...rows, { ...row, rowId: `xr-${row.sku}-${rows.length}` }];
	}
	return rows.map((r, i) => (i === at ? { ...r, qty: r.qty + row.qty } : r));
}
